import Phaser from "phaser";
import type { VfxParticlePreset } from "./types";

function parseColor(raw: string): number {
  return Number.parseInt(raw.replace(/^0x/i, ""), 16);
}

/** Stateless particle renderer driven by preset data. */
export function playParticles(
  scene: Phaser.Scene,
  x: number,
  y: number,
  preset: VfxParticlePreset,
  angle = 0,
  colorOverride?: number,
  depthOverride?: number,
): void {
  const color =
    colorOverride !== undefined ? colorOverride : parseColor(preset.color);
  const alpha = preset.alphaFrom ?? 0.9;
  const depth = depthOverride ?? preset.depth ?? 4;

  if (preset.kind === "streak") {
    const len = preset.length ?? 20;
    const half = len * 0.5;
    const n = Math.max(1, preset.count);
    const fan = n <= 1 ? 0 : 0.28;
    const g = scene.add.graphics().setDepth(depth);
    for (let i = 0; i < n; i++) {
      const u = n === 1 ? 0 : (i / (n - 1) - 0.5) * 2;
      const ang = angle + u * fan;
      const cx = x - Math.cos(ang) * (half * 0.35);
      const cy = y - Math.sin(ang) * (half * 0.35);
      g.lineStyle(Math.max(1, preset.radius * 2), color, alpha);
      g.beginPath();
      g.moveTo(cx - Math.cos(ang) * half, cy - Math.sin(ang) * half);
      g.lineTo(cx + Math.cos(ang) * half, cy + Math.sin(ang) * half);
      g.strokePath();
      g.fillStyle(color, alpha);
      g.fillCircle(
        cx + Math.cos(ang) * half * 0.85,
        cy + Math.sin(ang) * half * 0.85,
        Math.max(1.2, preset.radius),
      );
    }
    scene.tweens.add({
      targets: g,
      alpha: 0,
      duration: preset.duration,
      onComplete: () => g.destroy(),
    });
    return;
  }

  if (preset.kind === "ring") {
    const ring = scene.add.circle(x, y, preset.radius, color, alpha).setDepth(depth);
    if (preset.strokeColor) {
      ring.setStrokeStyle(
        preset.strokeWidth ?? 2,
        parseColor(preset.strokeColor),
        0.9,
      );
    }
    scene.tweens.add({
      targets: ring,
      scale: preset.scaleTo ?? 3,
      alpha: 0,
      duration: preset.duration,
      onComplete: () => ring.destroy(),
    });
    return;
  }

  // MMO-style sparkles: rise toward screen top (Phaser y decreases).
  if (preset.kind === "rise") {
    const n = Math.max(1, preset.count);
    const jitter = preset.spread ?? 16;
    const up = Math.max(8, preset.length ?? 40);
    for (let i = 0; i < n; i++) {
      const u = n === 1 ? 0 : i / (n - 1) - 0.5;
      const sx = x + u * jitter * 2 + (Math.random() - 0.5) * jitter * 0.6;
      const sy = y + (Math.random() - 0.3) * 8;
      const p = scene.add
        .circle(sx, sy, preset.radius, color, alpha)
        .setDepth(depth);
      const drift = (Math.random() - 0.5) * jitter * 0.5;
      scene.tweens.add({
        targets: p,
        x: sx + drift,
        y: sy - up * (0.75 + Math.random() * 0.35),
        alpha: 0,
        scale: preset.scaleTo ?? 0.35,
        duration: preset.duration * (0.85 + Math.random() * 0.3),
        ease: "Cubic.easeOut",
        onComplete: () => p.destroy(),
      });
    }
    return;
  }

  // radial burst
  const n = Math.max(1, preset.count);
  const spread = preset.spread ?? 30;
  for (let i = 0; i < n; i++) {
    const ang = (Math.PI * 2 * i) / n;
    const p = scene.add.circle(x, y, preset.radius, color, alpha).setDepth(depth);
    scene.tweens.add({
      targets: p,
      x: x + Math.cos(ang) * spread,
      y: y + Math.sin(ang) * spread,
      alpha: 0,
      duration: preset.duration,
      onComplete: () => p.destroy(),
    });
  }
}
